import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Database, Key, ScrollText, Server } from 'lucide-react';

const KIND_ICON = {
  'IAM role': Key,
  'S3 bucket': Database,
  'Secrets': Key,
  'Log group': ScrollText,
  'Compute': Server,
};

const SEV_COLOR = {
  critical: { hex: '#DC2626', text: 'text-apex-critical', bg: 'bg-red-500/10', border: 'border-red-500/40' },
  high:     { hex: '#D97706', text: 'text-amber-600',     bg: 'bg-amber-500/10', border: 'border-amber-500/40' },
  medium:   { hex: '#0EA5E9', text: 'text-sky-500',       bg: 'bg-sky-500/10', border: 'border-sky-500/40' },
};

export default function BlastRadiusGraph({ graph }) {
  if (!graph) return null;
  const { source, targets } = graph;

  return (
    <div data-testid="blast-radius-graph" className="relative">
      <div className="grid grid-cols-1 md:grid-cols-[200px,1fr] gap-4 items-stretch">
        {/* Source node */}
        <div className="relative">
          <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3 backdrop-blur-sm h-full flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-7 h-7 rounded-md bg-red-500/15 text-apex-critical inline-flex items-center justify-center">
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-apex-critical">Compromised</span>
            </div>
            <div className="text-[12px] font-semibold text-white leading-tight">{source.label}</div>
            <div className="text-[10px] font-mono text-slate-400 mt-0.5">{source.kind}</div>
            {/* Pulsing ring */}
            <span className="absolute inset-0 rounded-xl border border-apex-critical/40 animate-pulse pointer-events-none" />
          </div>
        </div>

        {/* Targets fan-out */}
        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {/* Connection lines container — absolute behind the cards on md+ */}
          <svg className="hidden md:block absolute -left-4 top-0 w-4 h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 16 100">
            {targets.map((_t, i) => {
              const yPct = ((i + 0.5) / targets.length) * 100;
              return (
                <line
                  key={i}
                  x1="0" y1="50"
                  x2="16" y2={yPct}
                  stroke={SEV_COLOR[targets[i].severity]?.hex ?? '#94A3B8'}
                  strokeWidth="1.5"
                  strokeOpacity="0.5"
                />
              );
            })}
          </svg>

          {targets.map((t, i) => {
            const Icon = KIND_ICON[t.kind] ?? Server;
            const sev = SEV_COLOR[t.severity] ?? SEV_COLOR.medium;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.08 }}
                data-testid={`blast-target-${t.id}`}
                className={`rounded-lg border ${sev.border} bg-slate-900/60 backdrop-blur-sm p-3`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`w-6 h-6 rounded-md ${sev.bg} ${sev.text} inline-flex items-center justify-center shrink-0`}>
                      <Icon className="w-3 h-3" />
                    </span>
                    <span className="text-[10px] uppercase tracking-wider font-mono text-slate-400">{t.kind}</span>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider font-bold ${sev.text}`}>{t.severity}</span>
                </div>
                <div className="text-[12px] font-semibold text-white leading-tight truncate" title={t.label}>{t.label}</div>
                <div className="mt-1.5 grid grid-cols-2 gap-1 text-[10px] font-mono">
                  <span className="text-slate-500">Data</span><span className="text-slate-300 text-right truncate">{t.data}</span>
                  <span className="text-slate-500">Path</span><span className="text-slate-300 text-right truncate">{t.via}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-3 gap-2 text-[10px] font-mono text-slate-400">
        <span><span className="text-apex-critical">{targets.filter(t => t.severity === 'critical').length}</span> critical</span>
        <span className="text-center"><span className="text-amber-400">{targets.filter(t => t.severity === 'high').length}</span> high</span>
        <span className="text-right"><span className="text-sky-400">{targets.filter(t => t.severity === 'medium').length}</span> medium</span>
      </div>
    </div>
  );
}
